import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BASE_PATH } from 'config';
import useRestartTour from 'hooks/useRestartTour.hooks';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    // Card,
    // CardContent,
    Chip,
    ClickAwayListener,
    Divider,
    // Grid,
    // InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    // OutlinedInput,
    Paper,
    Popper,
    Stack,
    // Switch,
    Typography,
    useMediaQuery
} from '@mui/material';

// third-party
import { FormattedMessage } from 'react-intl';
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import FormControlSelect from 'ui-component/extended/Form/FormControlSelect';
// import UpgradePlanCard from './UpgradePlanCard';
import HelpMenu from './HelpMenu';
import useAuth from 'hooks/useAuth';
import User1 from 'assets/images/users/knead_user1.png';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { useUserActiveRole, useCurrentUser } from 'hooks/useUser.hooks';
import { useOrganizations } from 'hooks/useOrganizations.hooks';

// assets
import {
    IconLogout,
    // IconSearch, 
    IconSettings,
    IconCreditCard,
    // IconUser,
    IconLock,
    IconRefresh,
    IconUserCircle,
    IconOutbound,
} from '@tabler/icons';
import useConfig from 'hooks/useConfig';
import ChangeEmailDialog from 'components/ChangeEmailDialog';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { borderRadius } = useConfig();
    const navigate = useNavigate();

    // const [sdm, setSdm] = useState(true);
    // const [value, setValue] = useState('');
    // const [notification, setNotification] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const { logout, user, updateUser } = useAuth();
    const [open, setOpen] = useState(false);
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const { restartTour } = useRestartTour();
    const organizationsState = useOrganizations();
    const userState = useCurrentUser();
    const editActiveRole = useUserActiveRole();
    const dispatch = useDispatch();

    const active_role_options = [
        { label: "Delivery Driver", value: "Delivery Driver" },
        { label: "Recipient", value: "Recipient" },
    ];

    const subscriptionAndBillingUrl = 'https://billing.stripe.com/p/login/00gfZ3bgB9WvbhScMM';

    // Handle Superuser or Super Admin return to their dashboard
    const handleReturnToDashboardClick = async () => {
        if (!organizationsState?.ready || !userState.ready) {
            return;
        }
        dispatch(
            openSnackbar({
                open: true,
                message: (userState.user?.assigned_role === 'Superuser' && !(userState.user.region != null && userState.user.super_organization != null)) ?
                        'Returning to the Superuser Dashboard, reloading page' : 'Returning to the Super-Organization Dashboard, reloading page',
                variant: 'alert',
                alert: {
                    color: 'success'
                },
                close: false
            })
        );
        await organizationsState.stopOrganizationAccess();
        window.location.href = BASE_PATH + '/dashboard';
    };

    // Handle changes in the user's active role (e.g. if a recipient user has delivery driver access)
    const handleChange = useCallback(
        async (value: string) => {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Setting active role, reloading.',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            await editActiveRole.changeActiveRole(value);
            window.location.href = BASE_PATH + '/dashboard';
        }, [],
    );

    /**
     * anchorRef is used on different components and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef<any>(null);
    const handleLogout = async () => {
        try {
            await logout(null);
        } catch (err) {
            console.error(err);
        }
    };

    const openInNewTab = (url: string): void => {
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
            newWindow.opener = null;
        }
    }
    const handleListItemClick = (event: React.MouseEvent<HTMLDivElement>, index: number, route: string = '') => {
        setSelectedIndex(index);
        handleClose(event);

        if (route && route !== '') {
            navigate(route);
        }
    };
    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };
    const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
        if ((anchorRef.current && anchorRef.current.contains(event.target))
            || ((event.target as HTMLElement).localName === 'body')) { // Keep menu open when accessing the "active role" dropdown form
            return;
        }
        setSelectedIndex(-1); //added this to remove ListButton highlight when popup closes


        setOpen(false);
    };
    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    const handleRestartTour = async () => {
        const success = await restartTour(); // Call the hook's function

        if (success) {
            await navigate('/dashboard'); // Redirect to the dashboard on success
            updateUser({ show_guided_onboarding: true });
        }
    };


    if (!userState.ready) {
        return <div>Loading</div>;
    }
    return (
        <>
            {((userState.user?.role === 'Admin' && userState.user?.assigned_role !== userState.user?.role)
                || (userState.user?.role === 'Super Admin' && userState.user?.assigned_role !== userState.user?.role)
            ) &&
                <Chip
                    label={
                        'Accessing ' + (userState.user.region ? userState.user.region.organization.name : userState.user.super_organization.name)
                        + '. Click here to return to the '
                        + ((userState.user?.assigned_role === 'Superuser' && !(userState.user.region != null && userState.user.super_organization != null))
                            ? 'Superuser Dashboard' : 'Super-Organization Dashboard')
                        + (isMobile ? ' \u2197' : '')
                    }
                    onClick={handleReturnToDashboardClick}
                    onDelete={handleReturnToDashboardClick}
                    deleteIcon={<IconOutbound />}
                    style={{
                        color: theme.palette.success.dark,
                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.success.light
                    }}
                    sx={{
                        marginRight: { xs: '0em', sm: '0.5em', md: '1em' },
                        marginLeft: { xs: '1.5em', sm: '1.5em' },
                        [theme.breakpoints.down('sm')]: { // Overflow text on mobile screens
                            flex: 1,
                            minWidth: 100,
                            overflow: 'hidden',
                            '& .MuiChip-label': {
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            },
                            '& .MuiChip-deleteIcon': {
                                display: 'none'
                            }
                        }
                    }}
                />
            }
            <HelpMenu />
            <Chip
                sx={{
                    height: '48px',
                    alignItems: 'center',
                    borderRadius: '27px',
                    transition: 'all .2s ease-in-out',
                    borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                    '&[aria-controls="menu-list-grow"], &:hover': {
                        borderColor: theme.palette.primary.main,
                        background: `${theme.palette.primary.main}!important`,
                        color: theme.palette.primary.light,
                        '& svg': {
                            stroke: theme.palette.primary.light
                        }
                    },
                    '& .MuiChip-label': {
                        lineHeight: 0
                    }
                }}
                icon={
                    <Avatar
                        src={User1}
                        sx={{
                            ...theme.typography.mediumAvatar,
                            margin: '8px 0 8px 8px !important',
                            cursor: 'pointer'
                        }}
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        color="inherit"
                        alt="user-images"
                    />
                }
                label={<IconSettings stroke={1.5} size="1.5rem" color={theme.palette.primary.main} />}
                variant="outlined"
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                color="primary"
                aria-label="user-account"
            />

            <Popper
                placement="bottom"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 14]
                        }
                    }
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions in={open} {...TransitionProps}>
                            <Paper>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Box sx={{ p: 2, pb: 0 }}>
                                            <Stack>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Typography variant="h4">Welcome back,</Typography>
                                                    <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                                                        {`${user?.first_name} ${user?.last_name}`}
                                                    </Typography>
                                                </Stack>

                                                {userState.user?.delivery_driver_access ? (
                                                    <div className="field field-active-role-selection" style={{ marginTop: '1em', marginBottom: '1em' }} >
                                                        <FormControlSelect onChange={handleChange}
                                                            options={active_role_options}
                                                            captionLabel="Your Active Role"
                                                            selected={user?.role}
                                                        />
                                                    </div>
                                                ) : (
                                                    <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>{user?.role}</Typography>
                                                )}

                                            </Stack>
                                            <Divider />
                                        </Box>
                                        <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}>
                                            <Box sx={{ p: 2, pt: 0 }}>
                                                <List
                                                    component="nav"
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: 350,
                                                        minWidth: 300,
                                                        backgroundColor: theme.palette.background.paper,
                                                        borderRadius: '10px',
                                                        [theme.breakpoints.down('md')]: {
                                                            minWidth: '100%'
                                                        },
                                                        '& .MuiListItemButton-root': {
                                                            mt: 0.5
                                                        }
                                                    }}
                                                >
                                                    {((user.role == "Food Donor") || (user.role == "Recipient") || (user.role == "Admin") || (user.role == "Delivery Driver")) &&
                                                        <ListItemButton
                                                            sx={{ borderRadius: `${borderRadius}px` }}
                                                            selected={selectedIndex === 0}
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                                handleListItemClick(event, 0, '/user/update-profile')
                                                            }
                                                        >
                                                            <ListItemIcon>
                                                                <IconUserCircle stroke={1.5} size="1.3rem" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={
                                                                    <Typography variant="body2">
                                                                        <FormattedMessage id="Update Profile" />
                                                                    </Typography>
                                                                }
                                                            />
                                                        </ListItemButton>
                                                    }
                                                    {((user.role == "Super Admin") || (
                                                        user.role == "Admin" && !(userState.user?.region?.organization.super_organization_id)
                                                    )) &&
                                                        <>
                                                            <ListItemButton
                                                                sx={{ borderRadius: `${borderRadius}px` }}
                                                                selected={selectedIndex === 1}
                                                                onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                                    openInNewTab(subscriptionAndBillingUrl)
                                                                }
                                                            >
                                                                <ListItemIcon>
                                                                    <IconCreditCard stroke={1.5} size="1.3rem" />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={
                                                                        <Typography variant="body2">
                                                                            <FormattedMessage id="Subscription & Billing" />
                                                                        </Typography>
                                                                    }
                                                                />
                                                            </ListItemButton>
                                                        </>
                                                    }
                                                    {(user.role == "Super Admin" || user.role == "Superuser") &&
                                                                <ListItemButton
                                                                sx={{ borderRadius: `${borderRadius}px` }}
                                                                selected={selectedIndex === 3}
                                                                onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                                    handleListItemClick(event, 3, '/2fa-settings')
                                                                }
                                                                    >
                                                                <ListItemIcon>
                                                                    <IconLock stroke={1.5} size="1.3rem" />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={
                                                                        <Typography variant="body2">
                                                                            Two-Factor Authentication
                                                                        </Typography>
                                                                    }
                                                                />
                                                                </ListItemButton>          
                                                    }
                                                    <ListItemButton
                                                        sx={{ borderRadius: `${borderRadius}px` }}
                                                        selected={selectedIndex === 4}
                                                        onClick={handleRestartTour}
                                                    >
                                                        <ListItemIcon>
                                                            <IconRefresh stroke={1.5} size="1.3rem" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="body2">
                                                                    <FormattedMessage id="Restart Product Tour" />
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItemButton>
                                                    <ListItemButton
                                                        sx={{ borderRadius: `${borderRadius}px` }}
                                                        selected={selectedIndex === 5}
                                                        onClick={handleLogout}
                                                    >
                                                        <ListItemIcon>
                                                            <IconLogout stroke={1.5} size="1.3rem" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="body2">
                                                                    <FormattedMessage id="logout" />
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItemButton>
                                                </List>
                                            </Box>
                                        </PerfectScrollbar>
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
            <ChangeEmailDialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} />
        </>
    );
};

export default ProfileSection;
